<template>
    <div class="transfer-selected">
        <div class="transfer-selected-header">
            <span class="transfer-selected-title">
                {{ product.Vehicle.TransferCategory }}
            </span>

            <Button
                icon="Bin"
                color="transparent"
                :ariaLabel="
                    t({
                        key: 'checkout.remove_product',
                        values: [t({ key: 'shared.transfer_product' })],
                    })
                "
                :onClick="onDelete"
                size="s" />
        </div>

        <div class="transfer-selected-content">
            <div class="transfer-selected-details">
                <TransferDetails :transfer="product" :showShieldText="false" />

                <ButtonLink :onClick="onDetails">
                    {{ t({ key: 'shared.transfer_details' }) }}
                </ButtonLink>
            </div>

            <div class="transfer-selected-logos">
                <LazyImage
                    :src="cleanTransferImage(product.Vehicle.Image)"
                    :alt="product.Vehicle.TransferCategory" />

                <LazyImage
                    :src="cleanTransferImage(product.Partner.Image)"
                    :alt="product.Partner.Name"
                    v-tippy="product.Partner.Name" />
            </div>
        </div>

        <div class="transfer-selected-footer">
            <span class="transfer-selected-cost">
                {{ getMoneyObjectAsString(product.Price) }}
            </span>
            <ButtonSecondary :onClick="onChange">
                {{ t({ key: 'shared.edit' }) }}
            </ButtonSecondary>
        </div>
    </div>
</template>

<script lang="ts">
    import { defineComponent, watch, PropType, computed } from 'vue';
    import { t } from '@shared/helpers/translation';
    import { isEmpty, isNil } from 'lodash';
    import { useStore } from '@/store';
    import { getMoneyObjectAsString } from '@shared/helpers/money';
    import {
        TransferProduct,
        Customer,
        ProductType,
        HotelProduct,
        FlightProduct,
    } from '@/api/Cart/cart.interfaces';
    import { ModalType } from '@/store/general/types';
    import {
        cleanTransferImage,
        getMainPassenger,
        getValidTransferProductId,
    } from '@/helpers/transfer/transfer';
    import { logStatsigCheckoutTransferInteraction } from '@/tracking/statsig/checkout.functions';
    import { TransferTrip, AnyTransferProduct } from '@/helpers/transfer/transfer.interfaces';
    import LazyImage from '@ui/components/LazyImage/LazyImage.vue';
    import Button from '@ui/components/Button/Button.vue';
    import ButtonSecondary from '@ui/components/Button/ButtonSecondary.vue';
    import ButtonLink from '@ui/components/Button/ButtonLink.vue';
    import TransferDetails from '@/components/checkoutPageV3/Sections/Addons/Transfer/Trips/Trip/Shared/TransferDetails/TransferDetails.vue';

    export default defineComponent({
        name: 'TransferSelected',
        methods: { t, getMoneyObjectAsString, cleanTransferImage },
        components: { TransferDetails, LazyImage, Button, ButtonSecondary, ButtonLink },
        props: {
            product: {
                type: Object as PropType<TransferProduct>,
                required: true,
            },
            trip: {
                type: Object as PropType<TransferTrip>,
                required: true,
            },
            onChangeSelected: Function,
        },
        setup(props) {
            const store = useStore();

            const customer = computed<Customer>(() => store.getters['cart/getCustomer']);

            const validProduct = computed<AnyTransferProduct>(() => {
                const validProductId = getValidTransferProductId(props.trip);
                return store.getters['cart/getProduct'](validProductId);
            });

            function onChange() {
                logStatsigCheckoutTransferInteraction('Edit', null, null);
                props.onChangeSelected && props.onChangeSelected();
            }

            function onDelete() {
                store.dispatch('general/setModal', {
                    type: ModalType.DeleteProduct,
                    product: props.product,
                });
            }

            function onDetails() {
                logStatsigCheckoutTransferInteraction('Details', null, null);
                store.dispatch('general/setModal', {
                    type: ModalType.Product,
                    productId: props.product.Id,
                });
            }

            function getPassengerName(
                product: AnyTransferProduct,
                prop: 'FirstName' | 'LastName'
            ): string | null {
                if (!product) return null;

                switch (product.Type) {
                    case ProductType.Hotel: {
                        const hotel = product as HotelProduct;
                        const room = hotel.Rooms[0];
                        return room ? room.Pax[prop] : null;
                    }

                    case ProductType.Flight: {
                        const flight = product as FlightProduct;
                        const adult = flight.Pax.find((x) => x.PaxDescription?.IsAdult);
                        return adult ? adult[prop] : null;
                    }

                    default:
                        return null;
                }
            }

            watch(
                () => ({
                    phone: customer.value.PhoneNumber,
                    email: customer.value.Email,
                    firstName: getPassengerName(validProduct.value, 'FirstName'),
                    lastName: getPassengerName(validProduct.value, 'LastName'),
                }),
                (newVal, oldVal) => {
                    if (
                        Object.values(newVal).some((value) => isNil(value) || isEmpty(value))
                    ) {
                        store.dispatch('cart/updateTransferMainPassenger', {
                            productId: props.product.Id,
                            mainPassenger: null,
                        });

                        return;
                    }

                    if (!oldVal || JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
                        store.dispatch('cart/updateTransferMainPassenger', {
                            productId: props.product.Id,
                            mainPassenger: getMainPassenger(
                                validProduct.value,
                                customer.value
                            ),
                        });
                    }
                }
            );

            return { onChange, onDelete, onDetails };
        },
    });
</script>

<style lang="scss" scoped>
    @forward './Selected.scss';
</style>
